
import { useMemo, useState } from 'react';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isEmpty } from 'lodash';
import { IAccountPreference, ISessionKey } from '@share/common-types';
import { FunctionalRootState, getSelectedCurrency, getSessionObj, UrlUtils } from '@share/utils';
import { SESSION_KEY_LABEL, SESSION_KEY_LABEL_ROOMS } from '@constants';
import { useDispatch, useSelector } from 'react-redux';
import { getBookPackageWithCard, getCarsBookPackageWithCard, ICarsDetailsState, ICarsReviewBookState, ICouponState, IReviewBookState } from '@store/slices';
import { IHotelsState, ILoginState, IMarginatorState } from '@share/store/slices';;
import moment from 'moment';
import { DATE_FORMAT_FILTER, GET_CUSTOMER_LOYALTY, QUOTE_LABEL } from '@share/constants';
import { StripeBtnTypes } from '../../utils/stripe';

interface IProps {
  totalPrice: number;
  formsRef: any;
  isDisabled?: boolean;
  type: string;
  scrollToError?: () => void;
  setMultiplePayments?: (payment: any) => void;
  paymentSelected: string;
}

// Tipo de las opciones de pago
type ExpressCheckoutPaymentMethodsOption = {
  amazonPay: 'auto' | 'never'; // 'auto' shows Amazon Pay if Stripe deems it optimal for conversion
  applePay: 'always' | 'never'; // 'always' shows Apple Pay on supported platforms
  googlePay: 'always' | 'never';  // 'always' shows Google Pay on supported platforms
  link: 'auto' | 'never'; // Show Link if Stripe's auto rules deem it optimal
  paypal: 'auto' | 'never'; // Show PayPal if  available and Stripe allows
};

export default function ExpressCheckoutButtons(props: IProps) {
  const { formsRef, isDisabled, type, setMultiplePayments, paymentSelected } = props;

  const [visibility, setVisibility] = useState('hidden');
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const reviewBookStore: IReviewBookState = useSelector(
    (state: FunctionalRootState) => state.reviewBookStore,
  );
  const loginStore: ILoginState = useSelector(
    (state: FunctionalRootState) => state.loginStore,
  );
  const marginatorStore: IMarginatorState = useSelector(
    (state: FunctionalRootState) => state.marginatorStore,
  );
  const carsReviewBookStore: ICarsReviewBookState = useSelector(
    (state: FunctionalRootState) => state.carsReviewBookStore,
  );
  const carsDetailsStore: ICarsDetailsState = useSelector(
    (state: FunctionalRootState) => state.carsDetailsStore,
  );

  const { account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const { coupon }: ICouponState = useSelector(
    (state: FunctionalRootState) => state.redeemCodeStore,
  );

  const { agencyNumber, referenceNumber }: IHotelsState = useSelector((state: FunctionalRootState) => state.hotelsStore);

  const { guests, bookingSummary } = reviewBookStore;
  const { stripe: stripeInfo, driver } = carsReviewBookStore;
  const { car } = carsDetailsStore;
  const [isPaymentElementReady, setPaymentElementReady] = useState(false);

  const buttonsPreferences = account?.preferences.filter(
    (x: IAccountPreference) => x.key === 'payment_buttons',
  );

  const onConfirm = async (event: any) => {

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (!isPaymentElementReady) {
      console.error("El Payment Element is not ready");
      return;
    }

    await elements?.submit();

    const values = UrlUtils.getValues();

    if (type === StripeBtnTypes.Cars) {
      const carsData: any = {
        id: car?.id,
        customer: {
          primary: {
            personName: {
              givenName: driver?.givenName,
              surname: driver?.surname,
            },
            telephone: {
              phoneTechType: '1',
              phoneNumber: driver?.phone,
            },
            email: driver?.email,
            address: {
              streetNmbr: stripeInfo?.addressLine,
              cityName: stripeInfo?.city,
              postalCode: stripeInfo?.zipCode,
              stateCode: stripeInfo?.state ? stripeInfo?.state : 'AK',
              stateProv: stripeInfo?.state ? stripeInfo?.state : 'AK',
              countryCode: driver?.country,
            },
            countryCode: stripeInfo?.country,
            birthDate: `${moment(driver?.birthDate, DATE_FORMAT_FILTER).format('yyyy-MM-DD')}`,
            language: 'EN',
          },
        },
        currency: account?.currency,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: {
            memberId: marginatorStore.impersonator.memberId,
          },
        },
        isCheckoutExpress: true,
      };

      dispatch(getCarsBookPackageWithCard(carsData, stripe, elements));
    }
    else if (type === StripeBtnTypes.Hotel) {
      const sessionKey = getSessionObj(
        values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
        values[SESSION_KEY_LABEL] as ISessionKey,
      );
      const getLeadPax = guests.find((guest) => guest.isMainGuest);

      const { formCustomFieldRef } = formsRef;

      let customFields: any = [];
      if (bookingSummary?.customFields) {
        const fields = [...bookingSummary.customFields];
        fields.map((item) => {
          const value = formCustomFieldRef.current.getFieldValue(item.id);
          if (value) {
            // item = { ...item, value: value };
            customFields.push({ type: item.type, label: item.label, value: value });
          }
        })
      }

      let quote;
      if (values[QUOTE_LABEL]) {
        quote = (values[QUOTE_LABEL] as string);
      }

      const data: any = {
        sessionKey,
        bookingPrice: bookingSummary?.bookingCard.bookingPrice.payNowPrice,
        hotelId: bookingSummary?.bookingCard.package.hotelId,
        leadPaxId: getLeadPax?.id,
        leadPaxRoomId: getLeadPax?.allocation,
        packageId: bookingSummary?.bookingCard.package.packageId,
        couponCode: coupon,
        marginator:
        {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: {
            memberId: marginatorStore.impersonator.memberId
          }
        },
        guests: guests.map((guest) => {
          const type: string = guest?.type;

          return {
            ...guest,
            namePrefix: !isEmpty(guest?.namePrefix) ? guest?.namePrefix : 'Mr',
            givenName: !isEmpty(guest?.givenName) ? guest?.givenName : 'TBV',
            surname: !isEmpty(guest?.surname) ? guest?.surname : 'TBV',
            type,
          };
        }),
        referenceNumber,
        agencyNumber,
        currency: getSelectedCurrency(loginStore?.account),
        quote,
        customFields: customFields ? customFields : null,
        isCheckoutExpress: true
      };

      const getCustomerLoyalty: string = localStorage.getItem(GET_CUSTOMER_LOYALTY) ?? '';
      if (!isEmpty(getCustomerLoyalty)) {
        data.customerLoyalty = getCustomerLoyalty;
      }

      dispatch(getBookPackageWithCard(data, stripe, elements));

    }
  };

  const onReady = (event: any) => {
    if (!event.availablePaymentMethods) {
      // No buttons will show
      return;
    } else {
      setVisibility('block');
      setPaymentElementReady(true);
      if (isEmpty(paymentSelected)) setMultiplePayments?.(event.availablePaymentMethods);
    }
  };

  function getPaymentMethods(
    value: string,
    paymentSelected: string
  ): ExpressCheckoutPaymentMethodsOption | {} {
    // If both `value` and `paymentSelected` are empty, return an empty object
    if (isEmpty(value) && isEmpty(paymentSelected)) {
      return {};
    }

    // If `value` is empty but `paymentSelected` has a value, enable only the selected method
    if (isEmpty(value)) {
      return {
        amazonPay: 'never',
        applePay: 'never',
        googlePay: 'never',
        link: 'never',
        paypal: 'never',
      };
    }

    // If `value` contains data, enable payment methods based on `value` and `paymentSelected`
    const enabledMethods = value.split(',');

    const methods: ExpressCheckoutPaymentMethodsOption = {
      amazonPay: enabledMethods.includes('amazon_pay')
        ? paymentSelected === 'amazon_pay' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      applePay: enabledMethods.includes('apple_pay')
        ? paymentSelected === 'apple_pay' || paymentSelected === ''
          ? 'always'
          : 'never'
        : 'never',
      googlePay: enabledMethods.includes('google_play')
        ? paymentSelected === 'google_play' || paymentSelected === ''
          ? 'always'
          : 'never'
        : 'never',
      link: enabledMethods.includes('link')
        ? paymentSelected === 'link' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      paypal: enabledMethods.includes('paypal')
        ? paymentSelected === 'paypal' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
    };

    return methods;
  }


  return (
    <div id="checkout-page" style={{ position: 'relative' }}>
      <div
        id="express-checkout-element"
        style={{
          display: visibility === 'hidden' ? 'none' : 'block',
          opacity: isDisabled ? 0.5 : 1, // visual desactivation of the botton
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {/* Generate a unique key based on the payment method */}
        {useMemo(() => {
          const key = JSON.stringify(
            getPaymentMethods(
              buttonsPreferences ? buttonsPreferences[0]?.value : '',
              paymentSelected
            )
          );

          return (
            <ExpressCheckoutElement
              key={key} // Cambia la clave para forzar la reinstanciación
              onConfirm={onConfirm}
              onReady={onReady}
              options={{
                paymentMethods: getPaymentMethods(
                  buttonsPreferences ? buttonsPreferences[0]?.value : '',
                  paymentSelected
                ),
                paymentMethodOrder: [
                  'applePay',
                  'googlePay',
                  'link',
                  'paypal',
                  'amazonPay',
                ], // Orden de preferencia
              }}
            />
          );
        }, [buttonsPreferences, paymentSelected])}
      </div>
      {isDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent', // Sin color para mantener la visibilidad
            cursor: 'not-allowed',
          }}
        />
      )}
    </div>
  );
}

